import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import SeriesCard from '@/components/SeriesCard';
import { useSeries } from '@/contexts/SeriesContext';
import { Loader2, Plus, Library, Crown, Filter, ChevronDown, Users, BookOpen, Sparkles, Lock, Star, Palette, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const SeriesListPage = () => {
  const navigate = useNavigate();
  const {
    seriesList,
    loading,
    error,
    seriesLimit,
    fetchUserSeries,
    canCreateSeries,
    clearError
  } = useSeries();

  const [statusFilter, setStatusFilter] = useState('all');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [featuredSeries, setFeaturedSeries] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);

  useEffect(() => {
    fetchUserSeries();
  }, [fetchUserSeries]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  // Fetch featured series for the preview (non-premium users)
  useEffect(() => {
    const fetchFeatured = async () => {
      if (seriesLimit.isPremium) return; // Don't fetch if premium

      setLoadingFeatured(true);
      try {
        const response = await axios.get(`${API}/v1/series/public/featured?limit=4`);
        setFeaturedSeries(response.data.series || []);
      } catch (err) {
        console.error('Failed to fetch featured series:', err);
      } finally {
        setLoadingFeatured(false);
      }
    };

    fetchFeatured();
  }, [seriesLimit.isPremium]);

  const handleCreateSeries = () => {
    if (!canCreateSeries()) {
      toast.error('Series limit reached', {
        description: 'Upgrade to Premium for unlimited series',
        action: { label: 'Upgrade', onClick: () => navigate('/pricing') }
      });
      return;
    }
    navigate('/series/create');
  };

  // Filter series by status
  const filteredSeries = seriesList.filter(series => {
    if (statusFilter === 'all') return true;
    return series.status === statusFilter;
  });

  if (loading && seriesList.length === 0) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading your series...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show premium preview for non-premium users
  if (!seriesLimit.isPremium) {
    return (
      <PageLayout>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-screen-xl mx-auto px-4 py-4 md:py-6 md:px-8">

            {/* Header */}
            <div className="rounded-2xl p-4 md:p-5 mb-6 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    Story Series
                    <span
                      className="px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1"
                      style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', color: '#92400E', fontFamily: 'Quicksand, sans-serif' }}
                    >
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  </h1>
                  <p className="text-sm mt-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Create connected stories with recurring characters
                  </p>
                </div>
              </div>
            </div>

            {/* Hero Section - Dark mode compatible */}
            <div
              className="relative rounded-2xl p-6 md:p-8 mb-6 overflow-hidden series-hero-banner"
              style={{
                background: 'var(--st-bg-surface)',
                border: '1px solid var(--st-border-light)'
              }}
            >
              {/* Gradient overlay that works in both modes */}
              <div
                className="absolute inset-0 opacity-50 dark:opacity-30"
                style={{
                  background: 'linear-gradient(135deg, rgba(129, 140, 248, 0.15) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(79, 70, 229, 0.05) 100%)'
                }}
              />

              <div className="relative z-10 max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)' }}
                  >
                    <Library className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      Build Your Story Universe
                    </h2>
                  </div>
                </div>
                <p className="text-base md:text-lg mb-6" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)', lineHeight: '1.6' }}>
                  Create a series of connected stories where your characters grow, evolve, and go on new adventures together. Perfect for bedtime story continuations!
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                    fontFamily: 'Quicksand, sans-serif',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  <Crown className="w-5 h-5" />
                  Unlock Series with Premium
                </button>
              </div>

              {/* Decorative elements */}
              <div className="absolute top-4 right-4 opacity-20 dark:opacity-10">
                <Sparkles className="w-24 h-24" style={{ color: '#818CF8' }} />
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                {
                  icon: Users,
                  title: 'Recurring Characters',
                  description: 'Your characters appear consistently across every book in the series with the same look and personality.',
                  color: '#8B5CF6'
                },
                {
                  icon: BookOpen,
                  title: 'Connected Stories',
                  description: 'Each new book builds on previous adventures, creating a rich narrative your child will love.',
                  color: '#3B82F6'
                },
                {
                  icon: Palette,
                  title: 'Consistent Art Style',
                  description: 'All books share the same visual style, making your series look professionally crafted.',
                  color: '#10B981'
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="rounded-2xl p-5 shadow-sm transition-all hover:shadow-md dark:hover:shadow-lg"
                  style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ background: `${feature.color}15` }}
                  >
                    <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
                  </div>
                  <h3 className="font-bold text-base mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)', lineHeight: '1.5' }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Featured Series from the Community */}
            <div className="rounded-2xl p-6 shadow-sm mb-8" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  <h3 className="font-bold text-lg" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    Featured Series from the Community
                  </h3>
                </div>
                {featuredSeries.length > 0 && (
                  <button
                    onClick={() => navigate('/explore?tab=series')}
                    className="flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
                    style={{ color: '#6366F1' }}
                  >
                    View all
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {loadingFeatured ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#6366F1' }} />
                </div>
              ) : featuredSeries.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {featuredSeries.slice(0, 4).map((series) => (
                      <div
                        key={series.id}
                        className="relative group cursor-pointer"
                        onClick={() => navigate(`/series/public/${series.id}`)}
                      >
                        <div
                          className="aspect-[3/4] rounded-xl relative overflow-hidden"
                          style={{ border: '2px solid rgba(0,0,0,0.05)' }}
                        >
                          {series.cover_image_url ? (
                            <img
                              src={getImageUrl(series.cover_image_url)}
                              alt={series.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' }}
                            >
                              <Library className="w-10 h-10" style={{ color: '#818CF8' }} />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                            <span className="text-white text-xs font-medium">
                              {series.book_count || 0} {series.book_count === 1 ? 'book' : 'books'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs font-medium mt-2 text-center line-clamp-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                          {series.title}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Show characters from the first series if available */}
                  {featuredSeries[0]?.characters?.length > 0 && (
                    <div className="flex items-center gap-3 mt-5 p-4 rounded-xl" style={{ background: 'var(--st-bg-muted)' }}>
                      <div className="flex -space-x-2">
                        {featuredSeries[0].characters.slice(0, 3).map((char, idx) => (
                          <div
                            key={char.id || idx}
                            className="w-8 h-8 rounded-full overflow-hidden border-2 border-white"
                            style={{ background: '#E0E7FF' }}
                          >
                            {char.portrait_url ? (
                              <img
                                src={getImageUrl(char.portrait_url)}
                                alt={char.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Users className="w-4 h-4" style={{ color: '#818CF8' }} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Characters maintain consistent appearances across all books
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Library className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--st-text-muted)' }} />
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    No featured series yet. Be the first to create one!
                  </p>
                </div>
              )}
            </div>

            {/* What's Included */}
            <div
              className="rounded-2xl p-6 mb-6"
              style={{
                background: 'var(--st-bg-surface)',
                border: '1px solid var(--st-border-light)'
              }}
            >
              <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                What's Included with Premium Series
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  'Unlimited series creation',
                  'Up to 10 books per series',
                  'Character portrait generation',
                  'World-building settings',
                  'v2 & v3 quality illustrations',
                  'Export and share options'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#10B981' }} />
                    <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center py-6">
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-xl text-lg"
                style={{
                  background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                  fontFamily: 'Quicksand, sans-serif',
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)'
                }}
              >
                <Crown className="w-6 h-6" />
                Start Creating Your Series
              </button>
              <p className="text-sm mt-3" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Upgrade to Premium to unlock this feature
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">

          {/* Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Series
                  <span
                    className="px-2 py-0.5 rounded text-xs font-bold"
                    style={{ background: '#E0E7FF', color: '#4F46E5', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    Beta
                  </span>
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {seriesList.length} {seriesList.length === 1 ? 'series' : 'series'} created
                </p>
              </div>
              <button
                onClick={handleCreateSeries}
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm"
                style={{
                  background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif'
                }}
              >
                <Plus className="w-4 h-4" />
                Create Series
              </button>
            </div>
          </div>

          {/* Series Limit Indicator - for free users */}
          {!seriesLimit.isPremium && (
            <div
              className="flex items-center justify-between p-4 rounded-2xl mb-6"
              style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'var(--st-bg-surface-hover)' }}
                >
                  <Library className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    <span style={{ color: 'var(--st-primary)' }}>{seriesLimit.total}</span> / {seriesLimit.max} series used
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Free plan limit
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)' }}
              >
                <Crown className="w-4 h-4" />
                Unlock Unlimited
              </button>
            </div>
          )}

          {/* Filter Row */}
          <div className="flex items-center gap-3 mb-6">
            <div className="relative">
              <button
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  border: statusFilter !== 'all' ? '1px solid var(--st-primary)' : '1px solid var(--st-border-light)',
                  background: statusFilter !== 'all' ? 'var(--st-bg-surface-hover)' : 'var(--st-bg-surface)',
                  color: 'var(--st-text-heading)'
                }}
              >
                <Filter className="w-4 h-4" style={{ color: statusFilter !== 'all' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                <span>
                  {{ all: 'All Status', active: 'Active', completed: 'Completed', hiatus: 'On Hiatus' }[statusFilter]}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${filterDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
              </button>

              {filterDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setFilterDropdownOpen(false)} />
                  <div className="absolute left-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '160px' }}>
                    {[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'hiatus', label: 'On Hiatus' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => { setStatusFilter(option.value); setFilterDropdownOpen(false); }}
                        className="w-full px-4 py-3 text-left text-sm font-medium transition-colors hover:opacity-80 flex items-center gap-2"
                        style={{
                          fontFamily: 'Quicksand, sans-serif',
                          color: statusFilter === option.value ? 'var(--st-primary)' : 'var(--st-text-heading)',
                          background: statusFilter === option.value ? 'var(--st-bg-surface-hover)' : 'transparent'
                        }}
                      >
                        {statusFilter === option.value && <span style={{ color: 'var(--st-primary)' }}>✓</span>}
                        <span className={statusFilter === option.value ? '' : 'ml-5'}>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Empty State */}
          {filteredSeries.length === 0 && !loading && (
            <div
              className="text-center py-16 rounded-2xl"
              style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
            >
              <div
                className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center"
                style={{ background: 'var(--st-bg-surface-hover)' }}
              >
                <Library className="w-10 h-10" style={{ color: '#FDBA74' }} />
              </div>
              <p className="text-xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {statusFilter !== 'all' ? 'No Series Found' : 'No Series Yet'}
              </p>
              <p className="text-sm mb-6 max-w-sm mx-auto" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                {statusFilter !== 'all'
                  ? 'Try a different filter'
                  : 'Create your first series with recurring characters and connected stories!'}
              </p>
              {statusFilter === 'all' && (
                <button
                  onClick={handleCreateSeries}
                  className="px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] hover:shadow-lg text-white"
                  style={{
                    background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  Create Your First Series
                </button>
              )}
            </div>
          )}

          {/* Series Grid */}
          {filteredSeries.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredSeries.map((series) => (
                <SeriesCard
                  key={series.id}
                  series={series}
                  onClick={() => navigate(`/series/${series.id}`)}
                />
              ))}
            </div>
          )}

          {/* Mobile Create Button */}
          <div className="sm:hidden fixed bottom-6 right-6">
            <button
              onClick={handleCreateSeries}
              className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                color: 'white'
              }}
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SeriesListPage;
